import React, { useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Collapse,
  IconButton,
  CircularProgress,
  List,
  ListItem,
  Chip,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Alert,
} from '@mui/material';
import { Link } from 'react-router-dom';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SettingsIcon from '@mui/icons-material/Settings';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import API from '../../services/api';
import MobileDialog from '../dialogs/MobileDialog';

const fetchClientInsights = async () => {
  const response = await API.get('/clients/insights/', {
    params: { ts: new Date().getTime() },
  });
  return response.data;
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('he-IL', {
    style: 'currency',
    currency: 'ILS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

const formatDate = (dateStr) => {
  if (!dateStr) return 'אף פעם';
  const d = new Date(dateStr);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

const HIGHLIGHT_OPTIONS = [
  { key: 'no_recent_payment', label: 'לא שילמו לאחרונה', color: '#fff3e0' },
  { key: 'highest_debt', label: 'חוב גבוה', color: '#ffebee' },
];

// ==================== Settings Dialog ====================
const InsightsSettingsDialog = ({ open, onClose, currentConfig, onSave }) => {
  const [months, setMonths] = useState(currentConfig.no_payment_months_threshold || 2);
  const [highDebtThreshold, setHighDebtThreshold] = useState(currentConfig.high_debt_threshold || '');
  const [highlightCategories, setHighlightCategories] = useState(
    currentConfig.row_highlight_categories || ['no_recent_payment', 'highest_debt']
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleToggleCategory = (key) => {
    setHighlightCategories((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleSave = async () => {
    if (months < 1 || months > 24) {
      setError('חודשים חייב להיות בין 1 ל-24');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const response = await API.post('/clients/insights/config/', {
        no_payment_months_threshold: months,
        high_debt_threshold: highDebtThreshold ? Number(highDebtThreshold) : 0,
        row_highlight_categories: highlightCategories,
      });
      onSave(response.data);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || 'שגיאה בשמירה');
    } finally {
      setSaving(false);
    }
  };

  return (
    <MobileDialog open={open} onClose={onClose} maxWidth="xs" fullWidth dir="rtl">
      <DialogTitle sx={{ fontWeight: 'bold' }}>הגדרות תובנות</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <TextField
          label="חודשים ללא תשלום"
          type="number"
          value={months}
          onChange={(e) => setMonths(Number(e.target.value))}
          onFocus={(e) => e.target.select()}
          inputProps={{ min: 1, max: 24, style: { direction: 'ltr' } }}
          fullWidth
          size="small"
          sx={{ mt: 1, mb: 2 }}
          helperText="לקוחות שלא שילמו מעל מספר חודשים זה יופיעו באזהרה"
        />

        <TextField
          label="סף חוב גבוה"
          type="number"
          value={highDebtThreshold}
          onChange={(e) => setHighDebtThreshold(e.target.value)}
          onFocus={(e) => e.target.select()}
          onWheel={(e) => e.target.blur()}
          inputProps={{ min: 0, style: { direction: 'ltr' } }}
          fullWidth
          size="small"
          sx={{ mb: 2 }}
          helperText='לקוחות עם חוב מעל סכום זה יסומנו כ"חוב גבוה". השאר ריק לחישוב אוטומטי'
        />

        <FormControl component="fieldset">
          <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 1 }}>
            צביעת שורות בטבלה
          </FormLabel>
          <FormGroup>
            {HIGHLIGHT_OPTIONS.map((opt) => (
              <FormControlLabel
                key={opt.key}
                control={
                  <Checkbox
                    checked={highlightCategories.includes(opt.key)}
                    onChange={() => handleToggleCategory(opt.key)}
                  />
                }
                label={opt.label}
              />
            ))}
          </FormGroup>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>ביטול</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'שמור'}
        </Button>
      </DialogActions>
    </MobileDialog>
  );
};

// ==================== Insight Card ====================
const InsightCard = ({ title, icon, color, bgColor, items, renderMetric, clientDetailAllowed, isLoading, totalCount, onShowAll }) => (
  <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%', overflow: 'hidden' }}>
    <Box
      dir="rtl"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 1,
        backgroundColor: bgColor,
        borderBottom: `2px solid ${color}`,
      }}
    >
      {icon}
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color, flex: 1, fontSize: '0.9rem', lineHeight: 1.2 }}>
        {title}
      </Typography>
      {totalCount > 0 && (
        <Chip
          label={totalCount}
          size="small"
          sx={{
            backgroundColor: color,
            color: '#fff',
            fontWeight: 'bold',
            fontSize: '0.7rem',
            height: 20,
            minWidth: 24,
            '& .MuiChip-label': { px: 0.8 },
          }}
        />
      )}
    </Box>
    <CardContent dir="rtl" sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 }, minHeight: 100 }}>
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
          <CircularProgress size={28} sx={{ color }} />
        </Box>
      ) : items.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
          אין נתונים
        </Typography>
      ) : (
        <List dense disablePadding>
          {items.map((item, index) => (
            <ListItem
              key={item.ClientBusinessID}
              disableGutters
              sx={{
                py: 0.3,
                borderBottom: index < items.length - 1 ? '1px solid #f0f0f0' : 'none',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textAlign: 'right' }}>
                {clientDetailAllowed ? (
                  <Link
                    to={`/client/${item.ClientBusinessID}`}
                    style={{ textDecoration: 'none', color: '#1976d2', fontWeight: 500, fontSize: '0.85rem' }}
                  >
                    <Typography variant="body2" noWrap sx={{ fontWeight: 500, color: '#1976d2' }}>
                      {item.ClientName}
                    </Typography>
                  </Link>
                ) : (
                  <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                    {item.ClientName}
                  </Typography>
                )}
              </Box>
              <Chip
                label={renderMetric(item)}
                size="small"
                sx={{
                  backgroundColor: bgColor,
                  color,
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                  flexShrink: 0,
                  height: 22,
                  direction: 'ltr',
                  '& .MuiChip-label': { px: 0.8 },
                }}
              />
            </ListItem>
          ))}
        </List>
      )}
      {!isLoading && totalCount > 10 && onShowAll && (
        <Box sx={{ textAlign: 'center', pt: 0.5 }}>
          <Typography
            variant="body2"
            onClick={onShowAll}
            sx={{
              color: '#1976d2',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.85rem',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            הצג הכל ({totalCount})
          </Typography>
        </Box>
      )}
    </CardContent>
  </Card>
);

// ==================== Insights Config ====================
const INSIGHTS_CONFIG = [
  {
    key: 'most_active',
    totalKey: 'most_active_total',
    title: 'הכי פעילים',
    icon: <TrendingUpIcon sx={{ color: '#2e7d32', fontSize: 20 }} />,
    color: '#2e7d32',
    bgColor: '#e8f5e9',
    renderMetric: (item) => `${item.transaction_count} פעולות`,
  },
  {
    key: 'least_active',
    totalKey: 'least_active_total',
    title: 'הכי פחות פעילים',
    icon: <TrendingDownIcon sx={{ color: '#9e9e9e', fontSize: 20 }} />,
    color: '#616161',
    bgColor: '#f5f5f5',
    renderMetric: (item) => `${item.transaction_count} פעולות`,
  },
  {
    key: 'highest_debt',
    totalKey: 'highest_debt_total',
    title: 'חוב גבוה',
    icon: <AccountBalanceIcon sx={{ color: '#d32f2f', fontSize: 20 }} />,
    color: '#d32f2f',
    bgColor: '#ffebee',
    renderMetric: (item) => formatCurrency(item.total_debt),
  },
  {
    key: 'no_recent_payment',
    totalKey: 'no_recent_payment_total',
    titleFn: (months) => `לא שילמו ${months} חודשים`,
    icon: <WarningAmberIcon sx={{ color: '#ed6c02', fontSize: 20 }} />,
    color: '#ed6c02',
    bgColor: '#fff3e0',
    renderMetric: (item) => item.last_payment_date ? formatDate(item.last_payment_date) : 'אף פעם',
  },
];

// ==================== Main Panel ====================
const ClientInsightsPanel = ({
  userId,
  noPaymentMonthsThreshold = 2,
  highDebtThreshold = 0,
  rowHighlightCategories = ['no_recent_payment', 'highest_debt'],
  clientDetailAllowed = true,
  onHighlightIdsChange,
  onShowAll,
  isManager = false,
}) => {
  const storageKey = `clients-insights-collapsed-${userId}`;
  const queryClient = useQueryClient();

  const [collapsed, setCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      return saved === 'true';
    } catch {
      return false;
    }
  });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [localThreshold, setLocalThreshold] = useState(noPaymentMonthsThreshold);
  const [localHighDebtThreshold, setLocalHighDebtThreshold] = useState(highDebtThreshold);
  const [localHighlightCategories, setLocalHighlightCategories] = useState(rowHighlightCategories);

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(storageKey, String(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['clients', 'insights'],
    queryFn: fetchClientInsights,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
    retry: 1,
  });

  if (error) {
    console.error('ClientsInsights API error:', error?.response?.status, error?.response?.data || error?.message);
  }

  // Pass highlight IDs to parent for row coloring
  const highlightIds = React.useMemo(() => {
    if (!data) return {};
    const ids = {};
    if (localHighlightCategories.includes('no_recent_payment') && data.no_recent_payment_ids) {
      ids.no_recent_payment = new Set(data.no_recent_payment_ids);
    }
    if (localHighlightCategories.includes('highest_debt') && data.highest_debt_ids) {
      ids.highest_debt = new Set(data.highest_debt_ids);
    }
    return ids;
  }, [data, localHighlightCategories]);

  React.useEffect(() => {
    if (onHighlightIdsChange) {
      onHighlightIdsChange(highlightIds);
    }
  }, [highlightIds, onHighlightIdsChange]);

  const handleSettingsSave = useCallback((responseData) => {
    const newConfig = responseData.config || {};
    setLocalThreshold(newConfig.no_payment_months_threshold || 2);
    setLocalHighDebtThreshold(newConfig.high_debt_threshold || 0);
    setLocalHighlightCategories(newConfig.row_highlight_categories || ['no_recent_payment', 'highest_debt']);
    queryClient.invalidateQueries({ queryKey: ['clients', 'insights'] });
  }, [queryClient]);

  const firstLoad = isLoading && !data;

  return (
    <Box sx={{ mb: 2, px: { xs: 0.75, sm: 0 } }}>
      <Box
        dir="rtl"
        sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', flex: 1 }} onClick={toggleCollapsed}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
            תובנות לקוחות
          </Typography>
          <IconButton size="small" sx={{ ml: 0.5 }}>
            {collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
          </IconButton>
        </Box>
        {isManager && (
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); setSettingsOpen(true); }}
            sx={{ color: 'text.secondary' }}
          >
            <SettingsIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Collapse in={!collapsed}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
            gap: 1.5,
            width: '100%',
          }}
        >
          {INSIGHTS_CONFIG.map((cfg, index) => (
            <Box key={index} sx={{ minWidth: 0 }}>
              <InsightCard
                title={cfg.titleFn ? cfg.titleFn(localThreshold) : cfg.title}
                icon={cfg.icon}
                color={cfg.color}
                bgColor={cfg.bgColor}
                items={data?.[cfg.key] || []}
                totalCount={data?.[cfg.totalKey] || 0}
                renderMetric={cfg.renderMetric}
                clientDetailAllowed={clientDetailAllowed}
                isLoading={firstLoad}
                onShowAll={onShowAll ? () => onShowAll(cfg.key) : undefined}
              />
            </Box>
          ))}
        </Box>
      </Collapse>

      {settingsOpen && (
        <InsightsSettingsDialog
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          currentConfig={{
            no_payment_months_threshold: localThreshold,
            high_debt_threshold: localHighDebtThreshold,
            row_highlight_categories: localHighlightCategories,
          }}
          onSave={handleSettingsSave}
        />
      )}
    </Box>
  );
};

export default ClientInsightsPanel;
